// Iter 82 / Phase E1.0 — Leave Configuration admin page.
// Tabbed page combining Fiscal Year + Leave Policy + Workflow Template
// masters. Holiday calendars get a dedicated page because they have
// nested entry editing. Tab parity with the rest of the admin surface.
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, PencilSimple, Trash, FloppyDisk, X, Calendar, Tag, Network, Wrench } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import LeaveAdminSetupChecklist from "../../components/leave/LeaveAdminSetupChecklist";  // Iter 110 · Setup wizard
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const TABS = [
    { key: "fiscal-year", label: "Fiscal Years", icon: Calendar },
    { key: "policy", label: "Leave Policies", icon: Tag },
    { key: "workflow", label: "Approval Workflows", icon: Network },
    // Iter 110 · Slice 1 (P1.A) — HR-visible reason master editor.
    { key: "adjustment-reason", label: "Adjustment Reasons", icon: Wrench },
];

const EMPTY_FY = { code: "", name: "", start_date: "", end_date: "", is_current: false, is_active: true };
const EMPTY_REASON = { code: "", label: "", description: "", is_active: true, sort_order: 100 };
const EMPTY_POLICY = {
    code: "", name: "", description: "",
    annual_allocation_days: 0, accrual_strategy: "manual_only", accrual_monthly_days: 0,
    carry_forward_allowed: false, carry_forward_limit_days: 0, carry_forward_expiry_months: 12,
    encashment_allowed: false, encashment_max_days_per_year: 0,
    max_consecutive_days: 0, min_notice_days: 0,
    supporting_document_required: false, supporting_document_threshold_days: 0,
    applies_to_employee_ids: [], applies_to_designations: [], applies_to_departments: [],
    applies_to_categories: [], applies_to_gender: null,
    includes_weekends: false, includes_holidays: false, is_lop: false, is_active: true,
};
const EMPTY_WF = {
    code: "", name: "", description: "",
    stages: [{ role: "reporting_manager", specific_user_id: null, is_optional: false, label: "Reporting Manager" }],
    is_default: false, is_active: true,
    applies_to_employee_ids: [], applies_to_departments: [], applies_to_leave_types: [],
    min_days_threshold: 0,
};

export default function LeaveConfiguration() {
    const { can } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Deep-link support: if the URL carries ?tab=…, honour it so the
    // Setup Checklist CTA lands on the correct sub-page.
    const initialTab = (() => {
        const t = searchParams.get("tab");
        return TABS.some((x) => x.key === t) ? t : "fiscal-year";
    })();
    const [tab, setTab] = useState(initialTab);
    const [meta, setMeta] = useState({ accrual_strategies: [], workflow_roles: [], fiscal_years: [] });
    const [fys, setFys] = useState([]);
    const [policies, setPolicies] = useState([]);
    const [workflows, setWorkflows] = useState([]);
    const [reasons, setReasons] = useState([]);  // Iter 110 · adjustment-reason master
    const [hrMeta, setHrMeta] = useState({ departments: [], designations: [], employment_categories: [] });

    // Dialog state
    const [editKind, setEditKind] = useState(null); // "fy" | "policy" | "wf" | "reason"
    const [editRow, setEditRow] = useState(null);
    const [draft, setDraft] = useState({});

    const reload = useCallback(async () => {
        try {
            const [m, fy, p, w, hm, rs] = await Promise.all([
                api.get("/leave/meta"),
                api.get("/leave/fiscal-year/list"),
                api.get("/leave/policy/list"),
                api.get("/leave/workflow/list"),
                api.get("/hr/employee/meta"),
                api.get("/leave/admin/adjustment-reason/list", { params: { include_inactive: true } }),
            ]);
            setMeta(m.data);
            setFys(fy.data.items || []);
            setPolicies(p.data.items || []);
            setWorkflows(w.data.items || []);
            setHrMeta(hm.data);
            setReasons(rs.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load leave configuration"));
        }
    }, []);

    useEffect(() => { Promise.resolve().then(() => { reload(); }); }, [reload]);

    const openCreate = (kind) => {
        setEditKind(kind);
        setEditRow(null);
        setDraft(
            kind === "fy" ? EMPTY_FY :
            kind === "policy" ? EMPTY_POLICY :
            kind === "reason" ? EMPTY_REASON :
            EMPTY_WF,
        );
    };
    const openEdit = (kind, row) => {
        setEditKind(kind);
        setEditRow(row);
        const template =
            kind === "fy" ? EMPTY_FY :
            kind === "policy" ? EMPTY_POLICY :
            kind === "reason" ? EMPTY_REASON :
            EMPTY_WF;
        setDraft({ ...template, ...row });
    };
    const closeDialog = () => { setEditKind(null); setEditRow(null); setDraft({}); };

    const save = async () => {
        try {
            const endpointMap = {
                fy: "/leave/fiscal-year",
                policy: "/leave/policy",
                wf: "/leave/workflow",
                reason: "/leave/admin/adjustment-reason",
            };
            const endpoint = endpointMap[editKind];
            const body = { ...draft };
            if (editKind === "policy") {
                body.code = (body.code || "").toUpperCase();
                if (body.applies_to_gender === "") body.applies_to_gender = null;
            }
            if (editKind === "reason") {
                body.code = (body.code || "").toLowerCase().trim();
                if (!body.code.match(/^[a-z][a-z0-9_]{1,63}$/)) {
                    toast.error("Reason code must be lowercase letters/numbers/underscores, 2-64 chars, starting with a letter.");
                    return;
                }
                if (!body.label || body.label.trim().length < 2) {
                    toast.error("Label is required.");
                    return;
                }
            }
            if (editRow) {
                // Reasons are keyed by `code`, everything else by `id`.
                const keyValue = editKind === "reason" ? editRow.code : editRow.id;
                if (editKind === "reason") {
                    // Only send patch-able fields (code is immutable).
                    const patch = {
                        label: body.label, description: body.description,
                        is_active: body.is_active, sort_order: body.sort_order,
                    };
                    await api.patch(`${endpoint}/${keyValue}`, patch);
                } else {
                    await api.patch(`${endpoint}/${keyValue}`, body);
                }
                toast.success("Updated.");
            } else {
                await api.post(endpoint, body);
                toast.success("Created.");
            }
            closeDialog();
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        }
    };

    const remove = async (kind, row) => {
        const endpointMap = {
            fy: "/leave/fiscal-year",
            policy: "/leave/policy",
            wf: "/leave/workflow",
            reason: "/leave/admin/adjustment-reason",
        };
        const endpoint = endpointMap[kind];
        const keyValue = kind === "reason" ? row.code : row.id;
        const label = kind === "reason" ? row.code : `${row.code} ${row.name || ""}`;
        if (!window.confirm(`Delete ${label}?`)) return;
        try {
            const { data } = await api.delete(`${endpoint}/${keyValue}`);
            if (kind === "reason") {
                toast.success(
                    data.physically_removed
                        ? "Reason removed."
                        : data.reason_in_use_count
                            ? `Reason is used by ${data.reason_in_use_count} ledger entries — deactivated instead of physically removed.`
                            : "Seed reason deactivated (kept for audit lineage).",
                );
            } else {
                toast.success(data.note || "Deleted.");
            }
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Delete failed"));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="leave-config-page">
            <PageHeader
                eyebrow="Leave Management Configuration"
                title="Leave Setup"
                description="Fiscal years, leave policies, and approval workflows. Holiday calendars are managed under their own page."
                actions={(
                    <Button variant="outline" onClick={() => navigate("/admin/leave/holiday-calendars")} data-testid="leave-config-go-calendars">
                        <Calendar size={14} className="mr-1" /> Holiday Calendars
                    </Button>
                )}
            />

            <div className="mt-5">
                <LeaveAdminSetupChecklist variant="compact" hideWhenComplete={false} />
            </div>

            <div className="mt-6 flex gap-2 border-b border-slate-200" data-testid="leave-config-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        data-testid={`tab-${t.key}`}
                        onClick={() => setTab(t.key)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 ${tab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                    >
                        <t.icon size={14} /> {t.label}
                    </button>
                ))}
            </div>

            <div className="mt-5">
                {tab === "fiscal-year" && (
                    <section className="bg-white rounded-xl border border-slate-200 p-5" data-testid="fy-section">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-sm font-semibold">Fiscal Years</h3>
                            {can("leave:admin") && <Button size="sm" onClick={() => openCreate("fy")} data-testid="fy-new-btn"><Plus size={14} className="mr-1" /> New Fiscal Year</Button>}
                        </div>
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Code</th>
                                    <th className="text-left px-3 py-2 font-medium">Name</th>
                                    <th className="text-left px-3 py-2 font-medium">Period</th>
                                    <th className="text-left px-3 py-2 font-medium">Status</th>
                                    <th className="w-24" />
                                </tr>
                            </thead>
                            <tbody>
                                {fys.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No fiscal years configured.</td></tr>}
                                {fys.map((f) => (
                                    <tr key={f.id} className="border-t border-slate-100">
                                        <td className="px-3 py-2 font-mono text-xs">{f.code}</td>
                                        <td className="px-3 py-2 text-slate-700">{f.name}</td>
                                        <td className="px-3 py-2 text-xs text-slate-500">{f.start_date} → {f.end_date}</td>
                                        <td className="px-3 py-2">
                                            {f.is_current && <Badge className="bg-emerald-100 text-emerald-700 mr-1">Current</Badge>}
                                            {!f.is_active && <Badge variant="secondary">Inactive</Badge>}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            {can("leave:admin") && (
                                                <>
                                                    <button onClick={() => openEdit("fy", f)} className="text-slate-500 hover:text-slate-700 mr-2"><PencilSimple size={14} /></button>
                                                    <button onClick={() => remove("fy", f)} className="text-rose-500 hover:text-rose-700"><Trash size={14} /></button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                )}

                {tab === "policy" && (
                    <section className="bg-white rounded-xl border border-slate-200 p-5" data-testid="policy-section">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-sm font-semibold">Leave Policies</h3>
                            {can("leave:admin") && <Button size="sm" onClick={() => openCreate("policy")} data-testid="policy-new-btn"><Plus size={14} className="mr-1" /> New Leave Policy</Button>}
                        </div>
                        <p className="text-xs text-slate-500 mb-3">Multiple policies can share the same code (e.g., two CL policies — one default, one for Sales). Resolution priority: <strong>Employee → Designation → Department → Employment Category → Default</strong>.</p>
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Code</th>
                                    <th className="text-left px-3 py-2 font-medium">Name</th>
                                    <th className="text-left px-3 py-2 font-medium">Allocation</th>
                                    <th className="text-left px-3 py-2 font-medium">Strategy</th>
                                    <th className="text-left px-3 py-2 font-medium">Scope</th>
                                    <th className="text-left px-3 py-2 font-medium">Flags</th>
                                    <th className="w-24" />
                                </tr>
                            </thead>
                            <tbody>
                                {policies.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-400">No leave policies yet.</td></tr>}
                                {policies.map((p) => {
                                    const scope = [];
                                    if (p.applies_to_employee_ids?.length) scope.push(`${p.applies_to_employee_ids.length} employee(s)`);
                                    if (p.applies_to_designations?.length) scope.push(`${p.applies_to_designations.length} desig`);
                                    if (p.applies_to_departments?.length) scope.push(`${p.applies_to_departments.length} dept`);
                                    if (p.applies_to_categories?.length) scope.push(`${p.applies_to_categories.length} cat`);
                                    if (p.applies_to_gender) scope.push(p.applies_to_gender);
                                    if (scope.length === 0) scope.push("Default");
                                    return (
                                        <tr key={p.id} className="border-t border-slate-100" data-testid={`policy-row-${p.id}`}>
                                            <td className="px-3 py-2 font-mono text-xs">{p.code}</td>
                                            <td className="px-3 py-2 text-slate-700">{p.name}</td>
                                            <td className="px-3 py-2 text-xs text-slate-600">{p.annual_allocation_days}d / yr</td>
                                            <td className="px-3 py-2 text-xs"><Badge variant="outline">{p.accrual_strategy}</Badge></td>
                                            <td className="px-3 py-2 text-xs text-slate-600">{scope.join(", ")}</td>
                                            <td className="px-3 py-2 text-xs">
                                                <div className="flex flex-wrap gap-1">
                                                    {p.is_lop && <Badge className="bg-rose-100 text-rose-700">LOP</Badge>}
                                                    {p.carry_forward_allowed && <Badge className="bg-sky-100 text-sky-700">CF</Badge>}
                                                    {p.encashment_allowed && <Badge className="bg-emerald-100 text-emerald-700">Encash</Badge>}
                                                    {!p.is_active && <Badge variant="secondary">Inactive</Badge>}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                {can("leave:admin") && (
                                                    <>
                                                        <button onClick={() => openEdit("policy", p)} className="text-slate-500 hover:text-slate-700 mr-2"><PencilSimple size={14} /></button>
                                                        <button onClick={() => remove("policy", p)} className="text-rose-500 hover:text-rose-700"><Trash size={14} /></button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </section>
                )}

                {tab === "workflow" && (
                    <section className="bg-white rounded-xl border border-slate-200 p-5" data-testid="workflow-section">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-sm font-semibold">Approval Workflows</h3>
                            {can("leave:admin") && <Button size="sm" onClick={() => openCreate("wf")} data-testid="wf-new-btn"><Plus size={14} className="mr-1" /> New Workflow</Button>}
                        </div>
                        <p className="text-xs text-slate-500 mb-3">Workflow priority: <strong>Employee → Department → Leave Type → Default</strong>. Exactly one default required for the engine to resolve.</p>
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Code</th>
                                    <th className="text-left px-3 py-2 font-medium">Name</th>
                                    <th className="text-left px-3 py-2 font-medium">Stages</th>
                                    <th className="text-left px-3 py-2 font-medium">Scope</th>
                                    <th className="text-left px-3 py-2 font-medium">Default</th>
                                    <th className="w-24" />
                                </tr>
                            </thead>
                            <tbody>
                                {workflows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">No workflows configured.</td></tr>}
                                {workflows.map((w) => (
                                    <tr key={w.id} className="border-t border-slate-100">
                                        <td className="px-3 py-2 font-mono text-xs">{w.code}</td>
                                        <td className="px-3 py-2 text-slate-700">{w.name}</td>
                                        <td className="px-3 py-2 text-xs text-slate-600">{w.stages.map((s) => s.label || s.role).join(" → ")}</td>
                                        <td className="px-3 py-2 text-xs text-slate-600">
                                            {[
                                                w.applies_to_employee_ids?.length && `${w.applies_to_employee_ids.length} emp`,
                                                w.applies_to_departments?.length && `${w.applies_to_departments.length} dept`,
                                                w.applies_to_leave_types?.length && `${w.applies_to_leave_types.length} type`,
                                                w.min_days_threshold > 0 && `> ${w.min_days_threshold}d`,
                                            ].filter(Boolean).join(", ") || "—"}
                                        </td>
                                        <td className="px-3 py-2">{w.is_default && <Badge className="bg-emerald-100 text-emerald-700">Default</Badge>}{!w.is_active && <Badge variant="secondary">Inactive</Badge>}</td>
                                        <td className="px-3 py-2 text-right">
                                            {can("leave:admin") && (
                                                <>
                                                    <button onClick={() => openEdit("wf", w)} className="text-slate-500 hover:text-slate-700 mr-2"><PencilSimple size={14} /></button>
                                                    <button onClick={() => remove("wf", w)} className="text-rose-500 hover:text-rose-700"><Trash size={14} /></button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                )}

                {/* Iter 110 · P1.A — Adjustment Reasons master */}
                {tab === "adjustment-reason" && (
                    <section className="bg-white rounded-xl border border-slate-200 p-5" data-testid="reason-section">
                        <div className="flex justify-between items-center mb-3">
                            <div>
                                <h3 className="text-sm font-semibold">Adjustment Reasons</h3>
                                <p className="text-xs text-slate-500 mt-1">
                                    Categorical reasons that HR must select when posting a manual leave-balance adjustment.
                                    System-seed reasons cannot be physically removed but can be deactivated to hide them from operators.
                                </p>
                            </div>
                            {can("leave:admin") && (
                                <Button size="sm" onClick={() => openCreate("reason")} data-testid="reason-new-btn">
                                    <Plus size={14} className="mr-1" /> New Reason
                                </Button>
                            )}
                        </div>
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Code</th>
                                    <th className="text-left px-3 py-2 font-medium">Label</th>
                                    <th className="text-left px-3 py-2 font-medium">Description</th>
                                    <th className="text-left px-3 py-2 font-medium w-24">Sort</th>
                                    <th className="text-left px-3 py-2 font-medium w-32">Status</th>
                                    <th className="w-24" />
                                </tr>
                            </thead>
                            <tbody>
                                {reasons.length === 0 && (
                                    <tr><td colSpan={6} className="py-8 text-center text-slate-400">No reasons configured yet.</td></tr>
                                )}
                                {reasons.map((r) => (
                                    <tr key={r.code} className="border-t border-slate-100" data-testid={`reason-row-${r.code}`}>
                                        <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                                        <td className="px-3 py-2 text-slate-700">{r.label}</td>
                                        <td className="px-3 py-2 text-xs text-slate-500">{r.description || "—"}</td>
                                        <td className="px-3 py-2 text-xs text-slate-500">{r.sort_order}</td>
                                        <td className="px-3 py-2 text-xs">
                                            {r.is_active ? <Badge className="bg-emerald-100 text-emerald-700">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
                                            {r.is_system_seed && <Badge className="ml-1 bg-slate-100 text-slate-600" title="System-seed row · protected from physical delete">Seed</Badge>}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            {can("leave:admin") && (
                                                <>
                                                    <button
                                                        onClick={() => openEdit("reason", r)}
                                                        className="text-slate-500 hover:text-slate-700 mr-2"
                                                        data-testid={`reason-edit-${r.code}`}
                                                    ><PencilSimple size={14} /></button>
                                                    <button
                                                        onClick={() => remove("reason", r)}
                                                        className="text-rose-500 hover:text-rose-700"
                                                        data-testid={`reason-delete-${r.code}`}
                                                    ><Trash size={14} /></button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                )}
            </div>

            {/* Iter 110 · P1.A — Adjustment Reason edit dialog */}
            <Dialog open={editKind === "reason"} onOpenChange={(o) => { if (!o) closeDialog(); }}>
                <DialogContent
                    data-testid="reason-dialog"
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                >
                    <DialogHeader><DialogTitle>{editRow ? "Edit Adjustment Reason" : "New Adjustment Reason"}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Code (lowercase, letters/numbers/underscores)</Label>
                            <Input
                                data-testid="reason-code"
                                value={draft.code || ""}
                                onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                                placeholder="e.g. compensation_credit"
                                disabled={!!editRow}  // Code is immutable after creation.
                            />
                            {editRow && <p className="text-[10px] text-slate-500 mt-1">Code is immutable — create a new reason if you need to rename.</p>}
                        </div>
                        <div>
                            <Label className="text-xs">Label (shown to operators in dropdowns)</Label>
                            <Input
                                data-testid="reason-label"
                                value={draft.label || ""}
                                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                                placeholder="e.g. Compensation credit"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Description (optional; shown as tooltip)</Label>
                            <Textarea
                                data-testid="reason-description"
                                rows={2}
                                value={draft.description || ""}
                                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                                placeholder="Brief guidance for operators explaining when this reason applies."
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Sort order (lower appears first)</Label>
                            <Input
                                type="number"
                                data-testid="reason-sort"
                                value={draft.sort_order ?? 100}
                                onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) })}
                            />
                        </div>
                        <label className="flex items-center gap-2 text-xs">
                            <input
                                type="checkbox"
                                data-testid="reason-active"
                                checked={draft.is_active !== false}
                                onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                            /> Active (operators see this reason in dropdowns)
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeDialog}><X size={14} className="mr-1" /> Cancel</Button>
                        <Button onClick={save} data-testid="reason-save"><FloppyDisk size={14} className="mr-1" /> Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit dialogs */}
            <Dialog open={editKind === "fy"} onOpenChange={(o) => { if (!o) closeDialog(); }}>
                <DialogContent
                    data-testid="fy-dialog"
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                >
                    <DialogHeader><DialogTitle>{editRow ? "Edit Fiscal Year" : "New Fiscal Year"}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label className="text-xs">Code (e.g. FY2026-27)</Label><Input data-testid="fy-code" value={draft.code || ""} onChange={(e) => setDraft({ ...draft, code: e.target.value })} placeholder="FY2026-27" /></div>
                        <div><Label className="text-xs">Name</Label><Input data-testid="fy-name" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs">Start Date</Label><Input type="date" data-testid="fy-start" value={draft.start_date || ""} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} /></div>
                            <div><Label className="text-xs">End Date</Label><Input type="date" data-testid="fy-end" value={draft.end_date || ""} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} /></div>
                        </div>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="fy-current" checked={!!draft.is_current} onChange={(e) => setDraft({ ...draft, is_current: e.target.checked })} /> Mark as current (only one FY can be current at a time)</label>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={draft.is_active !== false} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} /> Active</label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeDialog}><X size={14} className="mr-1" /> Cancel</Button>
                        <Button onClick={save} data-testid="fy-save"><FloppyDisk size={14} className="mr-1" /> Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={editKind === "policy"} onOpenChange={(o) => { if (!o) closeDialog(); }}>
                <DialogContent
                    className="max-w-3xl max-h-[85vh] overflow-y-auto"
                    data-testid="policy-dialog"
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                >
                    <DialogHeader><DialogTitle>{editRow ? "Edit Leave Policy" : "New Leave Policy"}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs">Code (uppercase)</Label><Input data-testid="policy-code" value={draft.code || ""} onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} placeholder="CL" /></div>
                            <div><Label className="text-xs">Name</Label><Input data-testid="policy-name" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
                        </div>
                        <div><Label className="text-xs">Description</Label><Textarea rows={2} value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-2">
                            <legend className="text-xs font-semibold px-1">Allocation & Accrual</legend>
                            <div className="grid grid-cols-3 gap-3">
                                <div><Label className="text-xs">Annual days</Label><Input type="number" step="0.5" value={draft.annual_allocation_days || 0} onChange={(e) => setDraft({ ...draft, annual_allocation_days: parseFloat(e.target.value) })} /></div>
                                <div><Label className="text-xs">Strategy</Label>
                                    <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={draft.accrual_strategy || "manual_only"} onChange={(e) => setDraft({ ...draft, accrual_strategy: e.target.value })}>
                                        {(meta.accrual_strategies || []).map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div><Label className="text-xs">Monthly accrual days</Label><Input type="number" step="0.5" value={draft.accrual_monthly_days || 0} onChange={(e) => setDraft({ ...draft, accrual_monthly_days: parseFloat(e.target.value) })} /></div>
                            </div>
                        </fieldset>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-2">
                            <legend className="text-xs font-semibold px-1">Carry Forward & Encashment</legend>
                            <div className="grid grid-cols-2 gap-3">
                                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!draft.carry_forward_allowed} onChange={(e) => setDraft({ ...draft, carry_forward_allowed: e.target.checked })} /> Carry forward allowed</label>
                                <div><Label className="text-xs">CF limit (days)</Label><Input type="number" step="0.5" value={draft.carry_forward_limit_days || 0} onChange={(e) => setDraft({ ...draft, carry_forward_limit_days: parseFloat(e.target.value) })} /></div>
                                <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!draft.encashment_allowed} onChange={(e) => setDraft({ ...draft, encashment_allowed: e.target.checked })} /> Encashment allowed</label>
                                <div><Label className="text-xs">Encash max/yr</Label><Input type="number" step="0.5" value={draft.encashment_max_days_per_year || 0} onChange={(e) => setDraft({ ...draft, encashment_max_days_per_year: parseFloat(e.target.value) })} /></div>
                            </div>
                        </fieldset>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-2">
                            <legend className="text-xs font-semibold px-1">Constraints</legend>
                            <div className="grid grid-cols-3 gap-3">
                                <div><Label className="text-xs">Max consecutive days (0=∞)</Label><Input type="number" value={draft.max_consecutive_days || 0} onChange={(e) => setDraft({ ...draft, max_consecutive_days: parseInt(e.target.value || "0", 10) })} /></div>
                                <div><Label className="text-xs">Min notice days</Label><Input type="number" value={draft.min_notice_days || 0} onChange={(e) => setDraft({ ...draft, min_notice_days: parseInt(e.target.value || "0", 10) })} /></div>
                                <div><Label className="text-xs">Doc threshold (days)</Label><Input type="number" step="0.5" value={draft.supporting_document_threshold_days || 0} onChange={(e) => setDraft({ ...draft, supporting_document_threshold_days: parseFloat(e.target.value) })} /></div>
                            </div>
                            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!draft.supporting_document_required} onChange={(e) => setDraft({ ...draft, supporting_document_required: e.target.checked })} /> Supporting document required</label>
                            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!draft.includes_weekends} onChange={(e) => setDraft({ ...draft, includes_weekends: e.target.checked })} /> Includes weekends in day count</label>
                            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!draft.includes_holidays} onChange={(e) => setDraft({ ...draft, includes_holidays: e.target.checked })} /> Includes holidays in day count</label>
                            <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="policy-is-lop" checked={!!draft.is_lop} onChange={(e) => setDraft({ ...draft, is_lop: e.target.checked })} /> Is Loss-of-Pay (no balance check; deducts salary later)</label>
                        </fieldset>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-2">
                            <legend className="text-xs font-semibold px-1">Scope (priority: Employee → Designation → Department → Category → Default)</legend>
                            <div><Label className="text-xs">Applies to departments</Label>
                                <MultiSelectChips options={(hrMeta.departments || []).map((d) => d.name)} value={draft.applies_to_departments || []} onChange={(v) => setDraft({ ...draft, applies_to_departments: v })} dataTestId="policy-deps" />
                            </div>
                            <div><Label className="text-xs">Applies to designations</Label>
                                <MultiSelectChips options={(hrMeta.designations || []).map((d) => d.name)} value={draft.applies_to_designations || []} onChange={(v) => setDraft({ ...draft, applies_to_designations: v })} dataTestId="policy-desigs" />
                            </div>
                            <div><Label className="text-xs">Applies to employment categories</Label>
                                <MultiSelectChips options={hrMeta.employment_categories || []} value={draft.applies_to_categories || []} onChange={(v) => setDraft({ ...draft, applies_to_categories: v })} dataTestId="policy-cats" />
                            </div>
                            <div><Label className="text-xs">Gender restriction</Label>
                                <select className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm bg-white" value={draft.applies_to_gender || ""} onChange={(e) => setDraft({ ...draft, applies_to_gender: e.target.value || null })}>
                                    <option value="">— none —</option><option value="Female">Female only</option><option value="Male">Male only</option>
                                </select>
                            </div>
                        </fieldset>

                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={draft.is_active !== false} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} /> Active</label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeDialog}><X size={14} className="mr-1" /> Cancel</Button>
                        <Button onClick={save} data-testid="policy-save"><FloppyDisk size={14} className="mr-1" /> Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={editKind === "wf"} onOpenChange={(o) => { if (!o) closeDialog(); }}>
                <DialogContent
                    className="max-w-3xl max-h-[85vh] overflow-y-auto"
                    data-testid="wf-dialog"
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                >
                    <DialogHeader><DialogTitle>{editRow ? "Edit Workflow" : "New Workflow"}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs">Code (lowercase)</Label><Input data-testid="wf-code" value={draft.code || ""} onChange={(e) => setDraft({ ...draft, code: e.target.value.toLowerCase() })} placeholder="standard" /></div>
                            <div><Label className="text-xs">Name</Label><Input data-testid="wf-name" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></div>
                        </div>
                        <div><Label className="text-xs">Description</Label><Textarea rows={2} value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-2">
                            <legend className="text-xs font-semibold px-1">Approval Stages</legend>
                            {(draft.stages || []).map((st, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50 p-2 rounded">
                                    <div className="col-span-1 text-xs font-bold text-slate-500">#{i + 1}</div>
                                    <div className="col-span-3">
                                        <select className="h-9 w-full rounded-md border border-slate-300 px-2 text-xs bg-white" value={st.role} onChange={(e) => { const ns = [...draft.stages]; ns[i] = { ...st, role: e.target.value }; setDraft({ ...draft, stages: ns }); }}>
                                            {(meta.workflow_roles || []).map((r) => <option key={r} value={r}>{r}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-span-5"><Input className="h-9 text-xs" placeholder="Label (optional)" value={st.label || ""} onChange={(e) => { const ns = [...draft.stages]; ns[i] = { ...st, label: e.target.value }; setDraft({ ...draft, stages: ns }); }} /></div>
                                    <label className="col-span-2 flex items-center gap-1 text-xs"><input type="checkbox" checked={!!st.is_optional} onChange={(e) => { const ns = [...draft.stages]; ns[i] = { ...st, is_optional: e.target.checked }; setDraft({ ...draft, stages: ns }); }} /> Optional</label>
                                    <button className="col-span-1 text-rose-500 hover:text-rose-700" onClick={() => setDraft({ ...draft, stages: draft.stages.filter((_, j) => j !== i) })}><Trash size={14} /></button>
                                </div>
                            ))}
                            <Button size="sm" variant="outline" data-testid="wf-add-stage" onClick={() => setDraft({ ...draft, stages: [...(draft.stages || []), { role: "reporting_manager", is_optional: false, label: "" }] })}><Plus size={12} className="mr-1" /> Add Stage</Button>
                        </fieldset>

                        <fieldset className="border border-slate-200 rounded-md p-3 space-y-2">
                            <legend className="text-xs font-semibold px-1">Scope (priority: Employee → Department → Leave Type → Default)</legend>
                            <div><Label className="text-xs">Applies to departments</Label>
                                <MultiSelectChips options={(hrMeta.departments || []).map((d) => d.name)} value={draft.applies_to_departments || []} onChange={(v) => setDraft({ ...draft, applies_to_departments: v })} dataTestId="wf-deps" />
                            </div>
                            <div><Label className="text-xs">Applies to leave-type codes</Label>
                                <MultiSelectChips options={[...new Set(policies.map((p) => p.code))]} value={draft.applies_to_leave_types || []} onChange={(v) => setDraft({ ...draft, applies_to_leave_types: v })} dataTestId="wf-types" />
                            </div>
                            <div><Label className="text-xs">Activate only for leave &gt; N days (0=disabled)</Label><Input type="number" value={draft.min_days_threshold || 0} onChange={(e) => setDraft({ ...draft, min_days_threshold: parseFloat(e.target.value) })} /></div>
                        </fieldset>

                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" data-testid="wf-default" checked={!!draft.is_default} onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })} /> Mark as default (only one)</label>
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={draft.is_active !== false} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} /> Active</label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeDialog}><X size={14} className="mr-1" /> Cancel</Button>
                        <Button onClick={save} data-testid="wf-save"><FloppyDisk size={14} className="mr-1" /> Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function MultiSelectChips({ options, value, onChange, dataTestId }) {
    const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
    return (
        <div className="flex flex-wrap gap-1.5 border border-slate-200 rounded-md p-2 bg-white" data-testid={dataTestId}>
            {options.length === 0 && <span className="text-xs text-slate-400">No options available</span>}
            {options.map((o) => (
                <button key={o} type="button" onClick={() => toggle(o)} className={`text-xs px-2 py-1 rounded-full border ${value.includes(o) ? "bg-indigo-100 border-indigo-300 text-indigo-700" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                    {o}
                </button>
            ))}
        </div>
    );
}
